import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { extractPriceCatalogDummyData } from "./priceListDummyService";
import {
  DOCUMENT_UPLOAD_ACCEPT,
  DOCUMENT_UPLOAD_FORMAT_LABEL,
  isDocumentUploadFile,
} from "../../../utils/documentUploadTypes";

const PriceCatalogUploadPage = ({ onBack, onSave }) => {
  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const handleRcDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isDocumentUploadFile(file)) return;
    setPdfFile(file);
    setExtractedData(null);
    setShowPdfPreview(false);
  };

  const handleExtract = async () => {
    if (!pdfFile) return;
    try {
      setLoading(true);
      const result = await extractPriceCatalogDummyData();
      setExtractedData(result);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!extractedData) return;
    onSave(extractedData);
  };

  const handleClearFile = () => {
    setPdfFile(null);
    setExtractedData(null);
    setShowPdfPreview(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <HeaderTitle titleText="Upload Price Catalog" />
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "space-between" }}>
        <Box
          sx={{
            flex: "1 1 420px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            // variant="h5"
            sx={{
              fontFamily:"gilroyBold",
              // fontWeight: 600,
              color: "#1e293b",
              mb: 1,
              fontSize: "18px",
            }}
          >
            Price Catalog 
          </Typography>

          <Box
            onDrop={handleRcDrop}
            onDragOver={handleDragOver}
            sx={{
              width:"500px",
              border: "2px dashed #cbd5e1",
              borderRadius: "12px",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f8fafc",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f1f5f9" },
              transition: "background-color 0.2s ease",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "#334155",
                mb: 0.5,
                wordBreak: "break-all",
              }}
            >
              {pdfFile ? pdfFile.name : "Drag and drop file here"}
            </Typography>

            <Typography variant="body2" sx={{ color: "#64748b", fontSize: 14, mb: 2 }}>
              (Supported: {DOCUMENT_UPLOAD_FORMAT_LABEL})
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                Browse
              </Button>

              {pdfFile && (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={() => setShowPdfPreview((prev) => !prev)}
                  >
                    {showPdfPreview ? "Hide Preview" : "Preview"}
                  </Button>

                  <Button
                    variant="text"
                    color="error"
                    size="medium"
                    sx={{
                      textTransform: "none",
                      fontSize: 14,
                      fontWeight: 500,
                      px: 2.5,
                    }}
                    onClick={handleClearFile}
                  >
                    Clear
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {showPdfPreview && pdfFile && (
        <Box
          sx={{
            mt: 2,
            p: 1,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "white",
            maxWidth: "720px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#075b97",
              fontWeight: 600,
              mb: 1,
              fontSize: 14,
              textAlign: "left",
            }}
          >
            PDF Preview:
          </Typography>
          <iframe
            src={URL.createObjectURL(pdfFile)}
            title="PDF Preview"
            width="100%"
            height="250px"
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            fontFamily:"gilroyBold",
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !pdfFile || loading ? 0.7 : 1,
          }}
          onClick={handleExtract}
          disabled={!pdfFile || loading}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : "Extract"}
        </Button>

        <Button
          variant="contained"
          size="small"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            fontFamily:"gilroyBold",
            textTransform: "none",
            px: 1,
            py: 1,
            borderRadius: 1.5,
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: !extractedData || loading ? 0.7 : 1,
          }}
          onClick={handleSave}
          disabled={!extractedData || loading}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default PriceCatalogUploadPage;
